import axios, { AxiosError, AxiosRequestConfig, Method } from 'axios';
import { logger, sanitize } from './logger';
import { getAccessToken } from './tokenService';
import config from '@config/config';
import axiosRetry from 'axios-retry';

export type RequestServiceType = {
  path: string;
  method: Method;
  record?: Record<string, unknown>;
  params?: Record<string, unknown>;
  additionalHeaders?: Record<string, unknown>;
  contentType?: string;
  tokenType?: 'ACCESS_TOKEN' | 'ADMIN_TOKEN';
  memberId?: string;
};

export type AxiosInstanceHeadersType = {
  contentType?: string;
  additionalHeaders?: Record<string, unknown>;
  token?: string;
};

export const prepareAndMakeRequest = async ({
  path,
  method,
  record,
  params,
  additionalHeaders,
  tokenType = 'ACCESS_TOKEN',
  contentType,
  memberId,
}: RequestServiceType) => {
  try {
    const token = await getAccessToken({ memberId, tokenType });
    const headers = getRequestHeaders({ token, additionalHeaders, contentType });
    const axiosConfig = {
      responseType: 'json',
      headers,
      params,
      baseURL: config.baseURLEndpoint,
      url: path,
      method,
      data: record,
    } as AxiosRequestConfig;
    return makeRequest(axiosConfig);
  } catch (error) {
    logger.error(
      'Request Service',
      `RequestService -> prepareAndMakeRequest`,
      `Error making request`,
      error
    );
    throw error;
  }
};

export const makeRequest = async (requestConfig: AxiosRequestConfig): Promise<any> => {
  try {
    logger.info(
      `Request Service -> makeRequest -> Request`,
      ` ${JSON.stringify(sanitize(requestConfig))}`
    );

    const axiosInstance = axios.create();

    axiosRetry(axiosInstance, {
      retries: 3,
      retryCondition: (error: AxiosError<unknown, any>) => {
        const isError = error.response?.status ? error.response?.status >= 500 : false;
        return axiosRetry.isNetworkOrIdempotentRequestError(error) || isError;
      },
      retryDelay: (retryCount: number) => {
        return axiosRetry.exponentialDelay(retryCount);
      },
    });

    const { data, status, statusText } = await axiosInstance(requestConfig);
    logger.info(
      `Request Service -> makeRequest -> Response -> {Status: ${status}, Status Text: ${statusText}}`,
      `{"Request": ${JSON.stringify(sanitize(requestConfig))} , "Response": ${JSON.stringify(sanitize(data))}}`
    );

    return data;
  } catch (error) {
    logger.error(
      'Request Service',
      `requestService -> makeRequest`,
      `Error making request -> ${JSON.stringify(sanitize(requestConfig))}`,
      error
    );
    throw error;
  }
};

export const getRequestHeaders = ({
  contentType = 'application/json',
  additionalHeaders,
  token,
}: AxiosInstanceHeadersType) => {
  return {
    'Content-Type': contentType,
    Authorization: `Bearer ${token}`,
    ...additionalHeaders,
  };
};
